import {FOCUSABLE_ELEMENTS_SELECTOR} from '../constants/focusableElementsSelector';

/**
 * Finds the last focusable element within a container element.
 *
 * This utility is particularly useful for focus management in forms, wizards,
 * and other components where you need to detect when the user has reached
 * the last interactive element.
 *
 * @param container - The container element to search within
 * @returns The last focusable element or null if none found
 *
 * @example
 * const formRef = useRef<HTMLDivElement>(null);
 *
 * const handleTabKey = (event: KeyboardEvent) => {
 *   const lastFocusable = findLastFocusableElement(formRef.current);
 *   if (document.activeElement === lastFocusable) {
 *     // User is on last element, handle tab navigation
 *   }
 * };
 */
export const findLastFocusableElement = (container: HTMLElement | null): HTMLElement | null => {
  if (!container) {
    return null;
  }

  const all = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR));
  // Visible if it has layout boxes or client rects (works for fixed/absolute too)
  const visible = all.filter(
    (el) => (el.offsetWidth || el.offsetHeight || el.getClientRects().length) > 0
  );
  return visible.length > 0 ? visible[visible.length - 1] : null;
};
